import React, { useEffect, useMemo, useState } from 'react';
import { Database, Play, Search, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import Layout from '../components/Layout';
import turmasService, { type TurmaResponse } from '../services/turmasService';
import {
  backupImportService,
  type BackupImportResponse,
  type BackupSourceTurma,
  type StudentMatchStrategy,
} from '../services/backupImportService';
import styles from './BackupImportPage.module.css';

const BackupImportPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [executeLoading, setExecuteLoading] = useState(false);
  const [sourceTurmas, setSourceTurmas] = useState<BackupSourceTurma[]>([]);
  const [destinationTurmas, setDestinationTurmas] = useState<TurmaResponse[]>([]);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [selectedGradeId, setSelectedGradeId] = useState('');
  const [selectedTurmaId, setSelectedTurmaId] = useState<number | null>(null);
  const [matchStrategy, setMatchStrategy] = useState<StudentMatchStrategy>('cpf_then_name');
  const [createMissingStudents, setCreateMissingStudents] = useState(true);
  const [updateStudentTurma, setUpdateStudentTurma] = useState(true);
  const [result, setResult] = useState<BackupImportResponse | null>(null);

  const sourceYears = useMemo(() => {
    const years = [...new Set(sourceTurmas.map((item) => item.year))];
    return years.sort((a, b) => b - a);
  }, [sourceTurmas]);

  const filteredSourceTurmas = useMemo(() => {
    if (!selectedYear) return sourceTurmas;
    return sourceTurmas.filter((item) => item.year === selectedYear);
  }, [sourceTurmas, selectedYear]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [backupTurmas, turmas] = await Promise.all([
        backupImportService.listSourceTurmas(),
        turmasService.findAll(),
      ]);

      setSourceTurmas(backupTurmas);
      setDestinationTurmas(turmas);

      if (backupTurmas.length > 0) {
        const latestYear = Math.max(...backupTurmas.map((item) => item.year));
        setSelectedYear(latestYear);
        const firstSource = backupTurmas.find((item) => item.year === latestYear) ?? backupTurmas[0];
        setSelectedGradeId(firstSource.gradeId);
      }

      if (turmas.length > 0) {
        setSelectedTurmaId(turmas[0].id);
      }
    } catch (error) {
      console.error('Erro ao carregar dados de importação:', error);
      toast.error('Erro ao carregar dados do backup');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (filteredSourceTurmas.length === 0) {
      setSelectedGradeId('');
      return;
    }

    const stillSelected = filteredSourceTurmas.some((item) => item.gradeId === selectedGradeId);
    if (!stillSelected) {
      setSelectedGradeId(filteredSourceTurmas[0].gradeId);
    }
  }, [filteredSourceTurmas, selectedGradeId]);

  const handlePreview = async () => {
    if (!selectedGradeId || !selectedTurmaId) {
      toast.error('Selecione a turma de origem e a turma de destino');
      return;
    }

    try {
      setPreviewLoading(true);
      const response = await backupImportService.preview({
        gradeId: selectedGradeId,
        turmaId: selectedTurmaId,
        matchStrategy,
        createMissingStudents,
      });
      setResult(response);
      toast.success('Pré-visualização gerada');
    } catch (error: any) {
      console.error('Erro ao gerar pré-visualização:', error);
      toast.error(error.response?.data?.message || 'Erro ao gerar pré-visualização');
    } finally {
      setPreviewLoading(false);
    }
  };

  const formatRole = (role: 'principal' | 'alternativo') =>
    role === 'principal' ? 'Financeiro (main)' : 'Solidário (alternativo)';

  const handleExecute = async () => {
    if (!selectedGradeId || !selectedTurmaId) {
      toast.error('Selecione a turma de origem e a turma de destino');
      return;
    }

    const confirmed = window.confirm(
      'Confirma executar a importação? O processo cria alunos/contratos e vínculos no Sagex.',
    );
    if (!confirmed) return;

    try {
      setExecuteLoading(true);
      const response = await backupImportService.execute({
        gradeId: selectedGradeId,
        turmaId: selectedTurmaId,
        matchStrategy,
        createMissingStudents,
        updateStudentTurma,
      });
      setResult(response);
      toast.success('Importação executada com sucesso');
    } catch (error: any) {
      console.error('Erro ao executar importação:', error);
      toast.error(error.response?.data?.message || 'Erro ao executar importação');
    } finally {
      setExecuteLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout title="Importação Backup">
        <div className={styles.container}>
          <p>Carregando configuração de importação...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Importação Backup">
      <div className={styles.container}>
        <div className={styles.header}>
          <h1 className={styles.title}>
            <Database size={28} />
            Importar Enrollments do Backup
          </h1>
          <button className={styles.secondaryButton} onClick={loadData} disabled={loading}>
            <RefreshCw size={16} />
            Recarregar
          </button>
        </div>

        <div className={styles.card}>
          <h2 className={styles.cardTitle}>Configuração</h2>
          <p className={styles.hint}>
            No contrato do Sagex: mainResponsible vira Responsável Financeiro e alternativeResponsible vira Responsável Solidário.
          </p>

          <div className={styles.grid}>
            <label className={styles.field}>
              Ano no backup
              <select
                value={selectedYear ?? ''}
                onChange={(event) => setSelectedYear(Number(event.target.value))}
              >
                {sourceYears.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </label>

            <label className={styles.field}>
              Turma origem (backup)
              <select
                value={selectedGradeId}
                onChange={(event) => setSelectedGradeId(event.target.value)}
              >
                {filteredSourceTurmas.map((turma) => (
                  <option key={turma.gradeId} value={turma.gradeId}>
                    {turma.label} - {turma.enrollments} enrollments
                  </option>
                ))}
              </select>
            </label>

            <label className={styles.field}>
              Turma destino (Sagex)
              <select
                value={selectedTurmaId ?? ''}
                onChange={(event) => setSelectedTurmaId(Number(event.target.value))}
              >
                {destinationTurmas.map((turma) => (
                  <option key={turma.id} value={turma.id}>
                    {turma.nome} ({turma.serie} - {turma.ano})
                  </option>
                ))}
              </select>
            </label>

            <label className={styles.field}>
              Estratégia de match
              <select
                value={matchStrategy}
                onChange={(event) => setMatchStrategy(event.target.value as StudentMatchStrategy)}
              >
                <option value="cpf_then_name">CPF e fallback por nome</option>
                <option value="name_only">Somente nome</option>
              </select>
            </label>
          </div>

          <div className={styles.options}>
            <label className={styles.checkbox}>
              <input
                type="checkbox"
                checked={createMissingStudents}
                onChange={(event) => setCreateMissingStudents(event.target.checked)}
              />
              Criar alunos faltantes automaticamente
            </label>

            <label className={styles.checkbox}>
              <input
                type="checkbox"
                checked={updateStudentTurma}
                onChange={(event) => setUpdateStudentTurma(event.target.checked)}
              />
              Atualizar turma dos alunos vinculados para a turma destino
            </label>
          </div>

          <div className={styles.actions}>
            <button
              className={styles.secondaryButton}
              onClick={handlePreview}
              disabled={previewLoading || executeLoading}
            >
              <Search size={16} />
              {previewLoading ? 'Gerando...' : 'Pré-visualizar'}
            </button>

            <button
              className={styles.primaryButton}
              onClick={handleExecute}
              disabled={previewLoading || executeLoading}
            >
              <Play size={16} />
              {executeLoading ? 'Executando...' : 'Executar importação'}
            </button>
          </div>
        </div>

        {result && (
          <div className={styles.card}>
            <h2 className={styles.cardTitle}>Resultado</h2>

            <div className={styles.summaryGrid}>
              <div className={styles.summaryItem}>
                <span>Total de enrollments</span>
                <strong>{result.summary.totalEnrollments}</strong>
              </div>
              <div className={styles.summaryItem}>
                <span>Alunos para criar</span>
                <strong>{result.summary.studentsToCreate}</strong>
              </div>
              <div className={styles.summaryItem}>
                <span>Contratos para inserir</span>
                <strong>{result.summary.contractsToInsert}</strong>
              </div>
              <div className={styles.summaryItem}>
                <span>Contratos já existentes</span>
                <strong>{result.summary.contractsAlreadyExist}</strong>
              </div>
              <div className={styles.summaryItem}>
                <span>Enrollments pendentes</span>
                <strong>{result.summary.unresolvedEnrollments}</strong>
              </div>
              <div className={styles.summaryItem}>
                <span>Vínculos de responsável faltantes</span>
                <strong>{result.summary.missingResponsibleLinks}</strong>
              </div>
              <div className={styles.summaryItem}>
                <span>Com responsável financeiro</span>
                <strong>{result.summary.contractsWithMainResponsible}</strong>
              </div>
              <div className={styles.summaryItem}>
                <span>Com responsável solidário</span>
                <strong>{result.summary.contractsWithAlternativeResponsible}</strong>
              </div>
              <div className={styles.summaryItem}>
                <span>Com ambos os papéis</span>
                <strong>{result.summary.contractsWithBothResponsibles}</strong>
              </div>
              <div className={styles.summaryItem}>
                <span>Conflito de papel</span>
                <strong>{result.summary.contractsWithRoleConflict}</strong>
              </div>
            </div>

            {result.execution && (
              <div className={styles.executionBox}>
                <h3>Execução</h3>
                <p>Alunos criados: {result.execution.createdStudents}</p>
                <p>Contratos inseridos: {result.execution.insertedContracts}</p>
                <p>Vínculos com responsáveis aplicados: {result.execution.linkedResponsibles}</p>
                <p>Alunos atualizados para turma destino: {result.execution.updatedStudentTurma}</p>
              </div>
            )}

            {result.unresolvedItems.length > 0 && (
              <div className={styles.listBlock}>
                <h3>Enrollments sem resolução (até 50)</h3>
                <ul>
                  {result.unresolvedItems.map((item) => (
                    <li key={item.enrollmentId}>
                      {item.studentName ?? 'Sem nome'} - {item.reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {result.missingResponsibleLinks.length > 0 && (
              <div className={styles.listBlock}>
                <h3>Responsáveis não encontrados no Sagex (até 50)</h3>
                <ul>
                  {result.missingResponsibleLinks.map((item) => (
                    <li key={`${item.enrollmentId}-${item.role}-${item.cpf}`}>
                      {item.nome ?? 'Sem nome'} ({item.cpf}) - {formatRole(item.role)}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {result.roleConflicts.length > 0 && (
              <div className={styles.listBlock}>
                <h3>Conflitos de diferenciação Financeiro/Solidário (até 50)</h3>
                <ul>
                  {result.roleConflicts.map((item) => (
                    <li key={item.enrollmentId}>
                      {item.studentName ?? 'Sem aluno'} - responsável mapeado: {item.responsavelNome ?? 'Sem nome'}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default BackupImportPage;
